/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Table,
  Card
} from 'react-bootstrap'
import BreadCrumb from '../../components/Label/Breadcrumb'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import Loading from '../../components/atoms/Loading'
import { BadgeStatusCOA } from '../../components/atoms/StatusLabel'
import { Download, Search, RotateCw } from 'lucide-react'
import {
  capitalizeFirstLetter,
  convertDateWithTime
} from '../../helpers/constants'
import api from '../../config/AxiosInstance'
import TablePagination from '../../components/atoms/Table/TablePagination'
import { Loader } from 'react-bootstrap-typeahead'
import ModalStockAdjustment from '../../components/modal/stockAdjustment/ModalStockAdjustment'

const STATUSES = [
  { value: 'match', label: 'Match' },
  { value: 'not_match', label: 'Not Match' },
  { value: 'uncheck', label: 'Unchecked' }
]

const TYPES = [
  { value: 'checker', label: 'Checker' },
  { value: 'pic', label: 'PIC' }
]

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#888',
  textTransform: 'uppercase',
  marginBottom: '0.2rem',
  letterSpacing: '0.5px'
}

const valueStyle = {
  fontSize: '1rem',
  fontWeight: '500',
  color: '#333'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const filterLabelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

function StockOpnameCycleCountDetail() {
  const { state } = useLocation()
  const { id } = useParams()
  const [loadingTable, setLoadingTable] = useState(false)
  const [countRender, setCountRender] = useState(0)
  const [closeTicket, setCloseTicket] = useState(false)
  const navigate = useNavigate()
  const [data, setData] = useState({
    values: {
      id: null,
      code: null,
      status: null,
      checker: null,
      pic: null,
      created_by: null,
      created_at: null,
      updated_by: null,
      updated_at: null
    },
    metadata: {
      per_page: 10,
      current_page: 1,
      total_row: 0,
      total_page: 0
    }
  })
  const [filter, setFilter] = useState({
    q: '',
    status: '',
    type: '',
    name: ''
  })
  const [loading, setLoading] = useState({
    page: false,
    download: false,
    closeTicket: false
  })

  const handleBack = () => {
    navigate('/stock-opname/cycle-count', {
      replace: true,
      state: {
        warehouse: state?.warehouse,
        status: state?.status || '',
        q: state?.q
      }
    })
  }

  const columns = [
    { key: 'slot', label: 'Slot' },
    { key: 'rack', label: 'Rak Name' },
    {
      key: 'rack_type',
      label: 'Rak Type',
      component: (value) =>
        value?.rack_type ? capitalizeFirstLetter(value?.rack_type) : '-'
    },
    { key: 'checker', label: 'Checker' },
    { key: 'pic', label: 'PIC' },
    {
      key: 'status',
      label: 'Status',
      component: (value) => {
        if (value?.status) {
          return <BadgeStatusCOA text={value?.status || 'match'} />
        }
        return '-'
      }
    },
    {
      key: 'action',
      label: 'Action',
      component: (value) => (
        <div key={value?.id}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              navigate(
                `/stock-opname/cycle-count/slot/detail/${id}/${value?.id}`,
                {
                  replace: true,
                  state: {
                    id: id,
                    code: data?.values?.code,
                    status: data?.values?.status,
                    ...state
                  }
                }
              )
            }}
          >
            <Search size={12} />
          </Button>
        </div>
      )
    }
  ]

  const handlePageChange = (data) => {
    setData((prevData) => ({
      ...prevData,
      metadata: {
        ...prevData.metadata,
        current_page: data.selected + 1
      }
    }))
    window.scrollTo(0, 0)
  }

  const handleChangeFilter = (e) => {
    const { name, value } = e.target

    setFilter((prevFilter) => {
      let updatedFilter = {
        ...prevFilter,
        [name]: value
      }

      // if type changes and name is not empty, reset name
      if (name === 'type' && prevFilter.name !== '') {
        updatedFilter.name = ''
      }

      return updatedFilter
    })
  }

  const handleOnChangeMeta = (e) => {
    setData((prevData) => ({
      ...prevData,
      metadata: {
        ...prevData.metadata,
        current_page: 1,
        per_page: parseInt(e.target.value, 10)
      }
    }))
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (data?.metadata?.current_page !== 1) {
        setData((prevData) => ({
          ...prevData,
          metadata: {
            ...prevData.metadata,
            current_page: 1
          }
        }))
      } else {
        fetchSlot()
      }
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [filter])

  const handleResetFilter = () => {
    const resetFilter = { status: '', type: '', name: '', q: '' }
    setFilter(resetFilter)
  }

  const fetchData = async (customFilter = filter) => {
    try {
      if (countRender === 0) {
        setLoading((prev) => ({
          ...prev,
          page: true
        }))
      }
      const usedFilter = customFilter
      let params = {
        page: data?.metadata?.current_page || 1,
        per_page: data?.metadata?.per_page || 10,
        q: usedFilter.q || '',
        status: usedFilter.status || null
      }
      if (usedFilter?.type !== '') {
        params = {
          ...params,
          [usedFilter?.type]: usedFilter?.name
        }
      }
      const res = await api.get(`/stock-opname/${id}`, {
        params
      })
      const { data: dataResponse, metadata: metadataResponse } = res.data
      setData({
        values: dataResponse,
        metadata: metadataResponse
      })
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
      handleBack()
    } finally {
      setLoading((prev) => ({
        ...prev,
        page: false
      }))
      setCountRender((prevCount) => prevCount + 1)
    }
  }

  const handleDownload = async () => {
    try {
      setLoading((prev) => ({
        ...prev,
        download: true
      }))
      const res = await api.get(`/stock-opname/${id}/export`, {
        responseType: 'arraybuffer'
      })
      const code = data?.values?.code
      const { status, data: dataResponse } = res
      if (status === 200) {
        const blob = new Blob([dataResponse], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `cycle_count_${code || '-'}.xlsx`)
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
    } finally {
      setLoading((prev) => ({
        ...prev,
        download: false
      }))
    }
  }

  const fetchSlot = async () => {
    try {
      setLoadingTable(true)
      const params = {
        q: filter?.q || '',
        per_page: data?.metadata?.per_page || 10,
        page: data?.metadata?.current_page || 1,
        status: filter?.status
      }
      const res = await api.get(`/stock-opname/${id}`, {
        params
      })
      const { data: dataResponse, metadata: metadataResponse } = res.data
      setData((prevData) => ({
        ...prevData,
        metadata: metadataResponse,
        values: {
          ...prevData.values,
          slots: dataResponse?.slots
        }
      }))
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
    } finally {
      setTimeout(() => {
        setLoadingTable(false)
      }, 500)
    }
  }

  const getProgressAccuracy = useMemo(() => {
    const progress = data?.values?.progress

    const checkerTotalSlot = Number(progress?.checker_progress?.total_slot) || 0
    const checkerTotalChecked =
      Number(progress?.checker_progress?.total_checked) || 0
    const checkerPercentage =
      checkerTotalSlot > 0
        ? Math.round((checkerTotalChecked / checkerTotalSlot) * 100)
        : 0

    const picTotalSlot = Number(progress?.pic_progress?.total_slot) || 0
    const picTotalChecked = Number(progress?.pic_progress?.total_checked) || 0
    const picPercentage =
      picTotalSlot > 0 ? Math.round((picTotalChecked / picTotalSlot) * 100) : 0

    const totalSlot = Number(progress?.precentage?.total_slot) || 0
    const locMatch = Number(progress?.location_accuracy?.match) || 0
    const locationAccuracyPercentage =
      totalSlot > 0 ? Math.round((locMatch / totalSlot) * 100) : 0

    const totalSystem = Number(progress?.stock_accuracy?.total_system) || 0
    const totalChecker = Number(progress?.stock_accuracy?.total_checker) || 0
    const stockAccuracyPercentage =
      totalSystem > 0 ? Math.round((totalChecker / totalSystem) * 100) : 0

    return {
      checkerLabel: `${checkerTotalChecked}/${checkerTotalSlot} (${checkerPercentage}%)`,
      checkerPercentage,
      picLabel: `${picTotalChecked}/${picTotalSlot} (${picPercentage}%)`,
      picPercentage,
      locationAccuracyPercentage: String(locationAccuracyPercentage),
      stockAccuracyPercentage: String(stockAccuracyPercentage)
    }
  }, [data])

  const getOptions = useMemo(() => {
    if (filter?.type === 'checker') {
      return data?.values?.user?.checker || []
    }
    if (filter?.type === 'pic') {
      return data?.values?.user?.pic || []
    }
    return []
  }, [filter?.type])

  const handleModalCloseTicket = () => {
    setCloseTicket(!closeTicket)
  }

  const handleSubmitCloseTicket = async () => {
    try {
      setLoading((prevLoading) => ({
        ...prevLoading,
        closeTicket: true
      }))
      const res = await api.put(`/stock-opname/${id}/closed`)
      const { status } = res
      if (status === 200) {
        toast.success('Successfully closed ticket', {
          position: 'top-right',
          style: { fontSize: '14px', fontWeight: '600' }
        })
        fetchData()
        handleModalCloseTicket()
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
    } finally {
      setLoading((prevLoading) => ({
        ...prevLoading,
        closeTicket: false
      }))
    }
  }

  useEffect(() => {
    fetchData()
  }, [id])

  useEffect(() => {
    if (data?.values?.slots?.length > 0) {
      fetchSlot()
    }
  }, [data?.metadata?.per_page, data?.metadata?.current_page])

  if (loading.page) {
    return <Loading />
  }

  const renderInfoItem = (label, value) => (
    <div className="d-flex flex-column">
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value || '-'}</span>
    </div>
  )

  return (
    <Container fluid className="px-4 mb-5">
      <Toaster />
      <Row className="mb-3">
        <Col md={9}>
          <BreadCrumb
            label={data?.values?.code || ''}
            link="/stock-opname/cycle-count"
            back="CYCLE COUNT"
            status={data?.values?.status || 'finished'}
            type="BadgeStatusPrList"
            stateValue={{
              warehouse_id: state?.warehouse_id,
              start_time: state?.start_time,
              end_time: state?.end_time,
              q: state?.q,
              status: state?.status
            }}
          />
        </Col>
        <Col md={3} className="mt-3 d-flex justify-content-end">
          <div className="d-flex gap-2 mt-1 align-items-center">
            <Button
              variant="light"
              size="md"
              className="text-secondary fw-bold px-3 border"
              onClick={() => handleResetFilter()}
              style={{ fontSize: '14px' }}
            >
              <RotateCw size={16} className="me-2" />
              Reset Filters
            </Button>
            {!['finish', 'closed', 'done'].includes(data?.values?.status) && (
              <Button
                variant="danger"
                size="md"
                onClick={handleModalCloseTicket}
                style={{ fontSize: '14px' }}
              >
                Close Ticket
              </Button>
            )}
            {data?.values?.status === 'done' && (
              <Button
                variant={loading.download ? 'secondary' : 'outline-warning'}
                size="md"
                className={loading.download ? 'opacity-25' : ''}
                disabled={loading.download}
                onClick={() => handleDownload()}
              >
                <div className="d-flex gap-2 align-items-center">
                  <Download size={14} />
                  <span className="fw-bold">
                    {loading.download ? 'Exporting...' : 'Export Excel'}
                  </span>
                </div>
              </Button>
            )}
          </div>
        </Col>
      </Row>

      <Card style={cardStyle}>
        <Row className="mb-4">
          <Col md={4}>
            <div style={labelStyle}>CHECKER PROGRESS</div>
            <div className="position-relative mt-2">
              <div
                className="rounded"
                style={{ height: '32px', backgroundColor: '#f1f3f5' }}
              >
                <div
                  className="rounded h-100 transition-all"
                  style={{
                    width: `${getProgressAccuracy?.checkerPercentage}%`,
                    backgroundColor: '#ff9800', // orange color from mockup
                    transition: 'width 0.5s ease-out'
                  }}
                />
              </div>
              <div className="position-absolute top-0 start-50 translate-middle-x h-100 d-flex align-items-center">
                <span
                  className="fw-bold"
                  style={{
                    fontSize: '12px',
                    color: '#ffffff',
                    mixBlendMode: 'difference'
                  }}
                >
                  {getProgressAccuracy?.checkerLabel}
                </span>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div style={labelStyle}>PIC PROGRESS</div>
            <div className="position-relative mt-2">
              <div
                className="rounded"
                style={{ height: '32px', backgroundColor: '#f1f3f5' }}
              >
                <div
                  className="rounded h-100 transition-all"
                  style={{
                    width: `${getProgressAccuracy?.picPercentage}%`,
                    backgroundColor: '#ff9800',
                    transition: 'width 0.5s ease-out'
                  }}
                />
              </div>
              <div className="position-absolute top-0 start-50 translate-middle-x h-100 d-flex align-items-center">
                <span
                  className="fw-bold"
                  style={{
                    fontSize: '12px',
                    color: '#ffffff',
                    mixBlendMode: 'difference'
                  }}
                >
                  {getProgressAccuracy?.picLabel}
                </span>
              </div>
            </div>
          </Col>
          <Col md={2}>
            <div style={labelStyle}>LOCATION ACCURACY</div>
            <div className="d-flex align-items-end mt-1">
              <span
                style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: '#333'
                }}
              >
                {getProgressAccuracy?.locationAccuracyPercentage}%
              </span>
            </div>
          </Col>
          <Col md={2}>
            <div style={labelStyle}>STOCK ACCURACY</div>
            <div className="d-flex align-items-end mt-1">
              <span
                style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: '#333'
                }}
              >
                {getProgressAccuracy?.stockAccuracyPercentage}%
              </span>
            </div>
          </Col>
        </Row>
        <hr className="text-muted opacity-25" />
        <Row className="mt-4">
          <Col md={4}>{renderInfoItem('WAREHOUSE', state?.warehouse_name)}</Col>
          <Col md={4}>{renderInfoItem('CHECKER', data?.values?.checker)}</Col>
          <Col md={4}>{renderInfoItem('PIC', data?.values?.pic)}</Col>
        </Row>
      </Card>

      <Card style={cardStyle}>
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label style={filterLabelStyle}>Status</Form.Label>
              <Form.Select
                name="status"
                aria-label="status"
                onChange={handleChangeFilter}
                value={filter?.status || ''}
                style={FormControlStyle}
                className="shadow-none"
              >
                <option value="">All Status</option>
                {STATUSES.map((status) => (
                  <option key={status?.value} value={status?.value}>
                    {status?.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={filterLabelStyle}>Type</Form.Label>
              <Form.Select
                name="type"
                aria-label="type"
                value={filter?.type || ''}
                onChange={handleChangeFilter}
                style={FormControlStyle}
                className="shadow-none"
              >
                <option value="">All Types</option>
                {TYPES.map((status) => (
                  <option key={status?.value} value={status?.value}>
                    {status?.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={filterLabelStyle}>Name</Form.Label>
              <Form.Select
                name="name"
                aria-label="name"
                disabled={filter?.type === ''}
                value={filter?.name || ''}
                onChange={handleChangeFilter}
                style={FormControlStyle}
                className="shadow-none"
              >
                <option value="">PIC/Checker</option>
                {getOptions?.map((option) => (
                  <option key={option?.id} value={option?.id}>
                    {option?.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={filterLabelStyle}>Search</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={16} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by Code..."
                  className="shadow-none"
                  name="q"
                  value={filter.q}
                  onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      fetchSlot()
                    }
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Card style={cardStyle}>
        <Row className="mb-3">
          <Col md={12} className="d-flex align-items-center">
            <h5 style={headerTitleStyle} className="mb-0">
              Slot List
            </h5>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Table responsive hover className="border-top mb-0">
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  {columns.map((column) => (
                    <th
                      className={`align-middle p-2 ${column.className ?? ''}`}
                      key={column.key}
                      style={tableHeaderStyle}
                    >
                      <span>{column.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadingTable && (
                  <tr key={`loading-${Math.random()}`}>
                    <td colSpan={columns.length} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loadingTable && !data?.values?.slots?.length && (
                  <tr key={`not-found-${Math.random()}`}>
                    <td colSpan={columns.length} className="text-center p-5">
                      <div className="text-muted">Data not found</div>
                    </td>
                  </tr>
                )}
                {!loadingTable &&
                  data?.values?.slots?.length > 0 &&
                  data?.values?.slots?.map((item, index) => (
                    <tr key={`${item.id}-${index}`}>
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={`align-middle p-2 ${
                            column.className ?? ''
                          }`}
                          style={{
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '0.9rem'
                          }}
                        >
                          {column.component && column.component(item)}
                          {!column.component && item[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Col>
        </Row>
        <div className="p-3 bg-light rounded-3 mt-3">
          <div className="d-flex gap-4 align-items-center">
            {data?.values?.detail?.map((user) => (
              <div key={user.id} className="d-flex align-items-center gap-2">
                <span className="fw-bold text-dark text-uppercase small">
                  {user?.name || '-'}:
                </span>
                <span className="fw-bold text-danger">
                  {user?.total_slot || 0}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3">
          <TablePagination
            meta={data.metadata}
            handlePageChange={handlePageChange}
            handleOnChangeMeta={handleOnChangeMeta}
          />
        </div>
      </Card>

      <Card style={cardStyle}>
        <Row>
          <Col md={3}>
            {renderInfoItem(
              'CREATED BY',
              data?.values?.created_by
                ? capitalizeFirstLetter(data?.values?.created_by)
                : '-'
            )}
          </Col>
          <Col md={3}>
            {renderInfoItem(
              'CREATED AT',
              data?.values?.created_at && data?.created_at !== '-'
                ? convertDateWithTime(
                    data?.values?.created_at,
                    'DD-MM-YYYY, HH:mm'
                  )
                : '-'
            )}
          </Col>
          <Col md={3}>
            {renderInfoItem(
              'LAST UPDATED BY',
              data?.values?.updated_by
                ? capitalizeFirstLetter(data?.values?.updated_by)
                : '-'
            )}
          </Col>
          <Col md={3}>
            {renderInfoItem(
              'LAST UPDATED AT',
              data?.values?.updated_at
                ? convertDateWithTime(
                    data?.values?.updated_at,
                    'DD-MM-YYYY, HH:mm'
                  )
                : '-'
            )}
          </Col>
        </Row>
      </Card>

      <ModalStockAdjustment
        show={closeTicket}
        onHide={handleModalCloseTicket}
        title=""
        size="md"
        backdrop="static"
      >
        <Modal.Body className="my-2">
          <>
            <div className="p-2 mt-3 text-center">
              <div className="d-flex justify-content-center">
                <svg
                  width="181"
                  height="180"
                  viewBox="0 0 181 180"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M90.5 0C40.7991 0 0.5 40.2991 0.5 90C0.5 139.701 40.7991 180 90.5 180C140.201 180 180.5 139.701 180.5 90C180.5 40.2991 140.201 0 90.5 0ZM90.5 164.732C49.2366 164.732 15.7679 131.263 15.7679 90C15.7679 48.7366 49.2366 15.2679 90.5 15.2679C131.763 15.2679 165.232 48.7366 165.232 90C165.232 131.263 131.763 164.732 90.5 164.732Z"
                    fill="#828282"
                  />
                  <path
                    d="M80.8574 125.357C80.8574 127.915 81.8734 130.367 83.6817 132.176C85.4901 133.984 87.9428 135 90.5003 135C93.0577 135 95.5104 133.984 97.3188 132.176C99.1272 130.367 100.143 127.915 100.143 125.357C100.143 122.8 99.1272 120.347 97.3188 118.539C95.5104 116.73 93.0577 115.714 90.5003 115.714C87.9428 115.714 85.4901 116.73 83.6817 118.539C81.8734 120.347 80.8574 122.8 80.8574 125.357ZM85.6789 102.857H95.3217C96.2056 102.857 96.9289 102.134 96.9289 101.25V46.6071C96.9289 45.7232 96.2056 45 95.3217 45H85.6789C84.7949 45 84.0717 45.7232 84.0717 46.6071V101.25C84.0717 102.134 84.7949 102.857 85.6789 102.857Z"
                    fill="#828282"
                  />
                </svg>
              </div>
              <div className="my-3 fw-bold">
                <p>Close Ticket?</p>
                <p>
                  This ticket will be closed and all on going process will be
                  terminated
                </p>
              </div>
            </div>
          </>
          <Modal.Footer className="mt-3" style={{ height: '20px' }}>
            <hr />
            <div className="d-flex justify-end gap-2">
              <Button
                id="cancel-close-ticket"
                variant="transparent"
                className="text-muted"
                onClick={handleModalCloseTicket}
                disabled={loading.closeTicket}
              >
                Cancel
              </Button>
              <Button
                id="save-close-ticket"
                variant={`${loading.closeTicket ? 'secondary' : 'danger'}`}
                className={`${loading.closeTicket ? 'disabled' : ''}`}
                onClick={() => handleSubmitCloseTicket()}
                disabled={loading.closeTicket}
              >
                {loading.closeTicket ? 'Loading...' : 'Close'}
              </Button>
            </div>
          </Modal.Footer>
        </Modal.Body>
      </ModalStockAdjustment>
    </Container>
  )
}
export default StockOpnameCycleCountDetail
